import { appConfig } from "@/config";
import { Button } from "@/modules/shadcn/ui/button";
import { Input } from "@/modules/shadcn/ui/input";
import { Label } from "@/modules/shadcn/ui/label";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import { orgRoleLabel } from "@/modules/account/constants/roleLabels";
import { createOrganizationSchema, OrgMemberRoleEnum } from "@repo/schema";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { useAuth } from "../../hooks";
import {
  useAcceptInvitationMutation,
  useCreateOrganizationMutation,
  useDeclineInvitationMutation,
  useListInvitationsQuery,
  useListOrganizationsQuery,
  useSelectOrganizationMutation,
} from "../../services";
import { fieldErrorsFromZod } from "../../utils";

export const SelectOrganizationPage = () => {
  const { t } = useTranslation(["auth", "common", "account"]);
  const { isAuthenticated, isPreAuth } = useAuth();
  // Refetch on mount — see OrgDropdown: never render another session's list.
  const { data: organizations, isLoading: orgsLoading } = useListOrganizationsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: invitations } = useListInvitationsQuery();
  const [selectOrg, { isLoading: selectingOrg }] = useSelectOrganizationMutation();
  const [createOrg, { isLoading: creatingOrg }] = useCreateOrganizationMutation();
  const [acceptInvite] = useAcceptInvitationMutation();
  const [declineInvite] = useDeclineInvitationMutation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgNameError, setOrgNameError] = useState<string | undefined>();

  if (isAuthenticated === false) {
    return <Navigate to={appConfig.web.routes.auth.login} replace />;
  }

  if (isAuthenticated && !isPreAuth) {
    return <Navigate to={appConfig.web.routes.main.projects} replace />;
  }

  const handleSelectOrg = async (organizationId: number) => {
    try {
      setError(null);
      await selectOrg({ organizationId }).unwrap();
    } catch {
      setError(t("selectOrganization.errors.select"));
    }
  };

  const handleCreateOrg = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const name = (formData.get("orgName") as string | null)?.trim() ?? "";
    const parsed = createOrganizationSchema.safeParse({ name });
    if (!parsed.success) {
      setOrgNameError(fieldErrorsFromZod(parsed.error).name);
      return;
    }
    setOrgNameError(undefined);

    try {
      setError(null);
      await createOrg(parsed.data).unwrap();
      setShowCreateForm(false);
    } catch {
      setError(t("selectOrganization.errors.create"));
    }
  };

  const handleAcceptInvite = async (id: number) => {
    try {
      setError(null);
      await acceptInvite(id).unwrap();
    } catch {
      setError(t("selectOrganization.errors.accept"));
    }
  };

  const handleDeclineInvite = async (id: number) => {
    try {
      setError(null);
      await declineInvite(id).unwrap();
    } catch {
      setError(t("selectOrganization.errors.decline"));
    }
  };

  if (orgsLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  const hasOrgs = organizations && organizations.length > 0;
  const hasInvitations = invitations && invitations.length > 0;

  return (
    <div className="flex h-screen flex-col items-center justify-center p-8">
      <div className="flex min-h-0 w-full max-w-[480px] flex-col gap-6">
        <div className="mb-2 flex shrink-0 flex-col items-center gap-2 text-center">
          <h2 className="text-4xl font-semibold tracking-tight text-gray-900">
            {t("selectOrganization.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("selectOrganization.subtitle")}
          </p>
        </div>

        {error && (
          <div className="shrink-0 rounded-md border border-destructive/20 bg-destructive/5 p-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {hasInvitations && (
          <div className="flex min-h-0 flex-col gap-3 rounded-lg border border-border p-4">
            <h4 className="shrink-0 text-2xl font-semibold tracking-tight">
              {t("selectOrganization.pendingInvitations")}
            </h4>
            <div className="flex min-h-0 flex-col gap-3 overflow-y-auto overscroll-contain">
              {invitations.map((inv) => (
                <div key={inv.id} className="shrink-0 rounded-md bg-gray-50 p-3">
                  <div className="flex flex-row items-center justify-between gap-3">
                    <div>
                      <p className="font-medium">{inv.organizationName}</p>
                      <p className="text-xs text-muted-foreground">
                        {t("selectOrganization.invitedAs", {
                          role: t(
                            orgRoleLabel[
                              inv.role === OrgMemberRoleEnum.ADMIN
                                ? OrgMemberRoleEnum.ADMIN
                                : OrgMemberRoleEnum.MEMBER
                            ],
                          ),
                        })}
                      </p>
                    </div>
                    <div className="flex flex-row gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleAcceptInvite(inv.id)}
                      >
                        {t("selectOrganization.accept")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeclineInvite(inv.id)}
                      >
                        {t("selectOrganization.decline")}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasOrgs && (
          <div className="flex min-h-0 flex-col gap-3 rounded-lg border border-border p-4">
            <h4 className="shrink-0 text-2xl font-semibold tracking-tight">
              {t("selectOrganization.yourOrganizations")}
            </h4>
            <div className="flex min-h-0 flex-col gap-3 overflow-y-auto overscroll-contain">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  className="w-full shrink-0 cursor-pointer rounded-md bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => handleSelectOrg(org.id)}
                  disabled={selectingOrg}
                >
                  <div className="flex flex-row items-center justify-between">
                    <span className="font-medium">{org.name}</span>
                    <span className="text-muted-foreground">&rarr;</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!showCreateForm ? (
          <Button
            variant={hasOrgs ? "outline" : "default"}
            onClick={() => setShowCreateForm(true)}
            className="mt-2 w-full shrink-0"
          >
            {t("selectOrganization.createNew")}
          </Button>
        ) : (
          <div className="flex shrink-0 flex-col gap-3 rounded-lg border border-border p-4">
            <h4 className="text-2xl font-semibold tracking-tight">
              {t("selectOrganization.createTitle")}
            </h4>
            <form onSubmit={handleCreateOrg} noValidate>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="orgName">{t("selectOrganization.nameLabel")}</Label>
                  <Input
                    id="orgName"
                    name="orgName"
                    placeholder={t("selectOrganization.namePlaceholder")}
                    aria-invalid={!!orgNameError || undefined}
                    onChange={() => setOrgNameError(undefined)}
                  />
                  {orgNameError && (
                    <p className="text-xs text-destructive">{orgNameError}</p>
                  )}
                </div>
                <div className="flex flex-row gap-2">
                  <Button type="submit" disabled={creatingOrg}>
                    {creatingOrg ? t("selectOrganization.creating") : t("common:actions.create")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    {t("common:actions.cancel")}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>

      <div className="mt-8 shrink-0 text-center text-xs text-gray-500">
        {t("common:footer.line")}
      </div>
    </div>
  );
};
